// Capas del mapa de Choferes en vivo, al estilo My Maps: cada una se prende y
// se apaga, y la elección queda guardada en el navegador. Los datos vienen de
// GET /v2/ops/map-layers y, los domicilios, de GET /v2/driver-home.

import { driverName } from "./driverName";

export type StopState = "PENDING" | "DONE" | "PROBLEM";

export type MapStore = { id: number; name: string; address: string | null; latitude: number; longitude: number };
export type MapWarehouse = { id: number; name: string; type: string | null; address: string | null; latitude: number; longitude: number };
export type MapZone = { id: number; name: string; rings: [number, number][][] };
export type MapStop = {
  id: number; route_id: number; route_type: string; route_status: string; sequence: number;
  type: "PICKUP" | "DELIVERY"; status: string; state: StopState; store_id?: number | null;
  driver_id: number | null; driver_name: string | null; driver_username: string | null;
  label: string | null; address: string | null; latitude: number | null; longitude: number | null;
  packages: number; problems: number;
};
export type MapHome = {
  driver_id: number; address: string; latitude: number; longitude: number;
  username: string; first_name: string | null; last_name: string | null; vehiculo: string | null;
};
export type MapLayersData = { date: string; stores: MapStore[]; warehouses: MapWarehouse[]; zones: MapZone[]; stops: MapStop[] };

export type LayerKey = "drivers" | "homes" | "stores" | "warehouses" | "zones" | "pickups" | "deliveries" | "turbo";
// Lo elegido en el mapa o en la lista (un chofer, una parada, una tienda…).
export type MapSel = { kind: LayerKey; id: number };

export const LAYERS: { key: LayerKey; label: string; color: string }[] = [
  { key: "drivers", label: "Choferes en vivo", color: "#4f8cff" },
  { key: "turbo", label: "Turbos de hoy", color: "#f0883e" },
  { key: "pickups", label: "Colectas del día", color: "#a371f7" },
  { key: "deliveries", label: "Repartos del día", color: "#2f81f7" },
  { key: "warehouses", label: "Depósitos", color: "#e3b341" },
  { key: "stores", label: "Tiendas", color: "#db61a2" },
  { key: "zones", label: "Zonas", color: "#3fb950" },
  { key: "homes", label: "Domicilios de choferes", color: "#f0883e" },
];

export const STATE_LABEL: Record<StopState, string> = { PENDING: "Pendientes", DONE: "Hechas", PROBLEM: "Con problema" };
export const STATE_COLOR: Record<StopState, string> = { PENDING: "#d29922", DONE: "#3fb950", PROBLEM: "#f85149" };

// Paleta para distinguir zonas vecinas (no significa nada más que eso).
export const ZONE_COLORS = ["#3fb950", "#2f81f7", "#a371f7", "#db61a2", "#e3b341", "#f0883e", "#39c5cf", "#8b949e"];

// Qué choferes se ven (en el mapa y en la lista): todos, los que llevan un
// Turbo ahora, los habilitados para Turbo, o los que el coordinador tildó a
// mano («elegidos»). Un coordinador de Turbo sigue a cuatro o cinco motos,
// no a los ochenta choferes del reparto.
export type DriverFilter = "ALL" | "TURBO" | "ENABLED" | "CHOSEN";
export const DRIVER_FILTERS: { key: DriverFilter; label: string }[] = [
  { key: "ALL", label: "Todos" },
  { key: "TURBO", label: "Con Turbo" },
  { key: "ENABLED", label: "Habilitados Turbo" },
  { key: "CHOSEN", label: "Elegidos" },
];

export function filterDrivers<T extends { driver_id: number; turbo_route_id?: number | null; turbo_enabled?: boolean }>(
  choferes: T[], filter: DriverFilter, chosen: number[]
): T[] {
  if (filter === "TURBO") return choferes.filter((c) => c.turbo_route_id != null);
  if (filter === "ENABLED") return choferes.filter((c) => c.turbo_enabled === true || c.turbo_route_id != null);
  if (filter === "CHOSEN") { const set = new Set(chosen); return choferes.filter((c) => set.has(c.driver_id)); }
  return choferes;
}

// Cuántos de los elegidos están en el mapa: los que dejaron de reportar (o ya
// no están activos) no cuentan, ni un id repetido. Antes se contaba la lista
// guardada entera y «Elegidos 5» podía mostrar dos.
export function contarElegidos(choferes: { driver_id: number }[], chosen: number[]): number {
  const set = new Set(chosen);
  return new Set(choferes.filter((c) => set.has(c.driver_id)).map((c) => c.driver_id)).size;
}

// `view`: qué lista muestra el panel de la derecha («Ver: …»). `cluster`:
// agrupar los puntos cercanos al alejar el zoom. `driverFilter` y `chosen`:
// qué choferes se ven (ver DriverFilter).
export type LayerPrefs = {
  visible: Record<LayerKey, boolean>; states: Record<StopState, boolean>; view: LayerKey; cluster: boolean;
  driverFilter: DriverFilter; chosen: number[];
};

export const DEFAULT_PREFS: LayerPrefs = {
  visible: { drivers: true, turbo: true, pickups: true, deliveries: true, warehouses: true, stores: false, zones: false, homes: false },
  states: { PENDING: true, DONE: true, PROBLEM: true },
  view: "drivers",
  cluster: true,
  driverFilter: "ALL",
  chosen: [],
};

const PREFS_KEY = "lynx.mapaChoferes.capas";

// Lo guardado puede venir viejo o roto: se completa con los valores por defecto.
export function loadPrefs(): LayerPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || "null");
    if (!raw || typeof raw !== "object") return DEFAULT_PREFS;
    const pick = <T extends Record<string, boolean>>(base: T, saved: any): T =>
      Object.fromEntries(Object.keys(base).map((k) => [k, typeof saved?.[k] === "boolean" ? saved[k] : base[k]])) as T;
    const view = LAYERS.some((l) => l.key === raw.view) ? (raw.view as LayerKey) : DEFAULT_PREFS.view;
    const cluster = typeof raw.cluster === "boolean" ? raw.cluster : DEFAULT_PREFS.cluster;
    const driverFilter = DRIVER_FILTERS.some((f) => f.key === raw.driverFilter) ? (raw.driverFilter as DriverFilter) : DEFAULT_PREFS.driverFilter;
    const chosen: number[] = Array.isArray(raw.chosen) ? Array.from(new Set<number>(raw.chosen.map(Number).filter((n: number) => Number.isInteger(n) && n > 0))) : [];
    return { visible: pick(DEFAULT_PREFS.visible, raw.visible), states: pick(DEFAULT_PREFS.states, raw.states), view, cluster, driverFilter, chosen };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: LayerPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Sin almacenamiento (modo privado): la elección dura lo que dura la página.
  }
}

/* ===== Vistas armadas: prenden las capas y eligen la lista de una vez ===== */

export type ViewKey = "all" | "turbo" | "pickups" | "deliveries" | "drivers";
const on = (...keys: LayerKey[]) => Object.fromEntries(LAYERS.map((l) => [l.key, keys.includes(l.key)])) as Record<LayerKey, boolean>;

export const VIEWS: { key: ViewKey; label: string; visible: Record<LayerKey, boolean>; view: LayerKey }[] = [
  { key: "all", label: "Todo", visible: on("drivers", "turbo", "pickups", "deliveries", "warehouses", "stores", "zones", "homes"), view: "drivers" },
  // Lo que mira el coordinador de Turbo: las motos que llevan uno, las tiendas
  // con Turbo hoy y adónde va cada uno.
  { key: "turbo", label: "Turbo de hoy", visible: on("drivers", "turbo", "warehouses"), view: "turbo" },
  { key: "pickups", label: "Colectas de hoy", visible: on("drivers", "pickups", "stores", "warehouses"), view: "pickups" },
  { key: "deliveries", label: "Repartos de hoy", visible: on("drivers", "deliveries", "zones", "warehouses"), view: "deliveries" },
  { key: "drivers", label: "Solo choferes", visible: on("drivers", "warehouses"), view: "drivers" },
];

// Aplicar una vista vuelve a mostrar todos los estados (si no, una vista
// «Colectas» con «Hechas» apagado engaña) y respeta el agrupado elegido.
export function applyView(prefs: LayerPrefs, key: ViewKey): LayerPrefs {
  const v = VIEWS.find((x) => x.key === key);
  if (!v) return prefs;
  // La vista de Turbo deja en el mapa solo a los choferes que llevan uno; las
  // demás vuelven a mostrarlos a todos (la elección a mano se conserva).
  return { ...prefs, visible: { ...v.visible }, view: v.view, states: { ...DEFAULT_PREFS.states }, driverFilter: key === "turbo" ? "TURBO" : "ALL" };
}

// La vista armada que coincide con lo que se ve ahora (para marcar el botón).
// `hidden`: capas que no se ofrecen (domicilios sin permiso), no cuentan.
export function currentView(prefs: LayerPrefs, hidden: LayerKey[] = []): ViewKey | null {
  const allStates = Object.values(prefs.states).every(Boolean);
  const same = (v: (typeof VIEWS)[number]) =>
    v.view === prefs.view && LAYERS.every((l) => hidden.includes(l.key) || v.visible[l.key] === prefs.visible[l.key])
    && prefs.driverFilter === (v.key === "turbo" ? "TURBO" : "ALL");
  return allStates ? VIEWS.find(same)?.key ?? null : null;
}

/* ===== Agrupar puntos cercanos ===== */

// Por debajo de este zoom los puntos que caen cerca se dibujan como un
// círculo con la cantidad; desde acá en adelante, siempre sueltos.
export const CLUSTER_MAX_ZOOM = 15;
// Lado de la grilla en píxeles de pantalla: puntos en la misma celda van juntos.
export const CLUSTER_CELL_PX = 44;

// Agrupa por celdas de una grilla en píxeles (las coordenadas ya proyectadas
// al zoom actual). Sin dependencias: Leaflet viene del CDN y el plugin de
// clusters no dibuja en canvas. Devuelve los grupos en el orden de entrada.
export function gridClusters<T>(points: { item: T; x: number; y: number }[], cellPx = CLUSTER_CELL_PX): T[][] {
  const cells = new Map<string, T[]>();
  for (const p of points) {
    const k = `${Math.floor(p.x / cellPx)}:${Math.floor(p.y / cellPx)}`;
    const cell = cells.get(k);
    if (cell) cell.push(p.item); else cells.set(k, [p.item]);
  }
  return Array.from(cells.values());
}

const located = (s: MapStop) => s.latitude != null && s.longitude != null;

// Paradas de un tipo que se dibujan con los estados elegidos.
export function visibleStops(stops: MapStop[], type: "PICKUP" | "DELIVERY", states: Record<StopState, boolean>): MapStop[] {
  return stops.filter((s) => s.type === type && located(s) && states[s.state]);
}

// Cuántas paradas de cada estado hay (las sin ubicación aparte: no se ven).
export function stopCounts(stops: MapStop[], type: "PICKUP" | "DELIVERY") {
  const of = stops.filter((s) => s.type === type);
  return {
    total: of.length,
    PENDING: of.filter((s) => s.state === "PENDING").length,
    DONE: of.filter((s) => s.state === "DONE").length,
    PROBLEM: of.filter((s) => s.state === "PROBLEM").length,
    unlocated: of.filter((s) => !located(s)).length,
  };
}


export function stopTooltip(s: MapStop): string {
  const tipo = s.type === "PICKUP" ? "Colecta" : "Entrega";
  const quien = s.driver_id ? s.driver_name || s.driver_username || `chofer ${s.driver_id}` : "sin chofer";
  const paquetes = s.packages ? ` · ${s.packages} paquete${s.packages === 1 ? "" : "s"}` : "";
  const problemas = s.problems ? ` · ${s.problems} con problema` : s.status === "SKIPPED" ? " · salteada" : "";
  return `${tipo} · ${s.label || "sin nombre"}${s.address ? ` (${s.address})` : ""} · recorrido #${s.route_id}, parada ${s.sequence} · ${quien} · ${STATE_LABEL[s.state].toLowerCase().replace(/s$/, "")}${paquetes}${problemas}`;
}

export function homeTooltip(h: MapHome): string {
  return `Domicilio de ${homeName(h)}${h.vehiculo ? ` (${h.vehiculo})` : ""} · ${h.address}`;
}

/* ===== Panel lateral: listas por tipo, con búsqueda y filtros ===== */

// Una fila del panel, igual para cualquier tipo: lo que se muestra, dónde
// está (para centrar el mapa) y con qué se busca.
export type ListRow = {
  kind: LayerKey; id: number; title: string; subtitle: string; color: string;
  lat: number | null; lng: number | null; search: string;
  driverId?: number | null; routeId?: number; state?: StopState;
};

export type ListFilters = { q: string; state: StopState | "ALL"; driverId: number | "ALL"; storeToday: "ALL" | "WITH" | "WITHOUT" };
export const DEFAULT_FILTERS: ListFilters = { q: "", state: "ALL", driverId: "ALL", storeToday: "ALL" };

// Sin mayúsculas ni acentos: «gomez» encuentra «Gómez».
export const normalize = (t: string) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const driverLabel = (s: MapStop) => (s.driver_id ? s.driver_name || s.driver_username || `chofer ${s.driver_id}` : "sin chofer");
export const homeName = (h: MapHome) => driverName(h, "sin chofer");

// Centro aproximado de una zona: el promedio de su primer anillo.
export function zoneCenter(z: MapZone): [number, number] | null {
  const ring = z.rings[0];
  if (!ring?.length) return null;
  return [ring.reduce((a, p) => a + p[0], 0) / ring.length, ring.reduce((a, p) => a + p[1], 0) / ring.length];
}

// Tiendas con colecta hoy: id → cantidad de paradas de retiro.
export function storesWithPickup(stops: MapStop[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const s of stops) if (s.type === "PICKUP" && s.store_id) m.set(s.store_id, (m.get(s.store_id) ?? 0) + 1);
  return m;
}

// Los choferes que tienen paradas hoy (para el filtro «Chofer»).
export function stopDrivers(stops: MapStop[]): { id: number; name: string }[] {
  const m = new Map<number, string>();
  for (const s of stops) if (s.driver_id && !m.has(s.driver_id)) m.set(s.driver_id, driverLabel(s));
  return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

export function stopRow(s: MapStop): ListRow {
  return {
    kind: s.type === "PICKUP" ? "pickups" : "deliveries", id: s.id, title: s.label || "sin nombre",
    subtitle: `#${s.route_id} · parada ${s.sequence} · ${driverLabel(s)} · ${STATE_LABEL[s.state].toLowerCase().replace(/s$/, "")}${s.packages ? ` · ${s.packages} paq.` : ""}`,
    color: STATE_COLOR[s.state], lat: s.latitude, lng: s.longitude, driverId: s.driver_id, routeId: s.route_id, state: s.state,
    search: normalize(`${s.label ?? ""} ${s.address ?? ""} ${driverLabel(s)} ${s.route_id}`),
  };
}

/* ===== Turbos de hoy (GET /v2/turbo/today): tiendas con Turbo y destinos ===== */

// PROGRAMADO: Mercado Libre lo prometió para otro día; sale a ofrecerse ese día.
export type TurboEstado = "SIN_CHOFER" | "PROGRAMADO" | "CON_CHOFER" | "EN_CAMINO" | "ENTREGADO" | "NO_ENTREGADO" | "CANCELADO";
export type TurboHoy = {
  delivery_id: number; tracking: string | number | null; store_id: number | null; store_name: string | null;
  tienda: { lat: number; lng: number } | null; destino: { lat: number; lng: number } | null;
  nombre_destinatario: string | null; direccion: string | null; localidad: string | null; codigo_postal?: string | number | null;
  entro_at: string | null; vence_at: string | null; seconds_to_deadline: number | null; distancia_km?: number | null;
  // Comprado antes del día que se mira: un abierto que se arrastra de ayer.
  de_dias_anteriores?: boolean;
  estado: TurboEstado; estado_at: string | null; detalle: string | null; terminal: boolean;
  chofer: { driver_id: number; username: string; first_name: string | null; last_name: string | null } | null;
  recorrido: { route_id: number; status: string; started_at?: string | null; completed_at?: string | null } | null;
  hitos: { pickup_arrived_at: string | null; collected_at: string | null; pickup_done_at: string | null; delivery_arrived_at: string | null; delivery_done_at: string | null };
  despacho: { route_id: number; status: string; route_status?: string | null; ring: number | null; warnings: string[]; offers_count: number; escalated_at: string | null; distance_km: string | number | null;
    // El día del recorrido (el prometido, en un programado) y el inicio de la franja de ML.
    scheduled_date?: string | null; window_start_at?: string | null } | null;
  // Solo en /live: el plan guardado del grupo del chofer (hora estimada de la
  // entrega y holgura contra el fin de la franja menos el margen; negativa = tarde).
  plan?: { group_id: number; eta: string | null; holgura_min: number | null; estimado: boolean } | null;
};
export const TURBO_ESTADO_LABEL: Record<TurboEstado, string> = {
  SIN_CHOFER: "sin chofer", PROGRAMADO: "programado", CON_CHOFER: "con chofer", EN_CAMINO: "en camino", ENTREGADO: "entregado", NO_ENTREGADO: "no entregado", CANCELADO: "cancelado",
};
export const TURBO_ESTADO_COLOR: Record<TurboEstado, string> = {
  SIN_CHOFER: "#f85149", PROGRAMADO: "#39c5cf", CON_CHOFER: "#d29922", EN_CAMINO: "#3fb950", ENTREGADO: "#8b949e", NO_ENTREGADO: "#f85149", CANCELADO: "#8b949e",
};
const horaCorta = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "");
export const turboChofer = (t: TurboHoy) => (t.chofer ? driverName(t.chofer) : "sin chofer");

// Tiendas que tienen Turbo hoy: id → cuántos (y cuántos siguen abiertos).
export type TurboStore = { id: number; name: string; lat: number; lng: number; total: number; abiertos: number };
export function turboStores(turbos: TurboHoy[]): TurboStore[] {
  const m = new Map<number, TurboStore>();
  for (const t of turbos) {
    if (!t.store_id || !t.tienda) continue;
    const s = m.get(t.store_id) ?? { id: t.store_id, name: t.store_name ?? `tienda ${t.store_id}`, lat: t.tienda.lat, lng: t.tienda.lng, total: 0, abiertos: 0 };
    s.total++; if (!t.terminal) s.abiertos++;
    m.set(t.store_id, s);
  }
  return Array.from(m.values());
}

export function turboTooltip(t: TurboHoy): string {
  const vence = t.vence_at ? ` · entregar antes de ${horaCorta(t.vence_at)}` : "";
  return `Turbo ${t.tracking ?? `#${t.delivery_id}`} · ${t.store_name ?? "tienda"} → ${t.nombre_destinatario ?? "destino"}${t.direccion ? ` (${t.direccion})` : ""} · ${TURBO_ESTADO_LABEL[t.estado]}${t.detalle ? `: ${t.detalle}` : ""} · ${turboChofer(t)}${vence}`;
}

export function turboRow(t: TurboHoy): ListRow {
  const p = t.destino ?? t.tienda;
  return {
    kind: "turbo", id: t.delivery_id, title: `${t.store_name ?? "tienda"} → ${t.nombre_destinatario ?? "destino"}`,
    subtitle: `${TURBO_ESTADO_LABEL[t.estado]}${t.estado_at ? ` ${horaCorta(t.estado_at)}` : ""} · ${turboChofer(t)}${t.vence_at ? ` · vence ${horaCorta(t.vence_at)}` : ""}${t.detalle ? ` · ${t.detalle}` : ""}`,
    color: TURBO_ESTADO_COLOR[t.estado], lat: p?.lat ?? null, lng: p?.lng ?? null, driverId: t.chofer?.driver_id ?? null, routeId: t.recorrido?.route_id,
    search: normalize(`${t.store_name ?? ""} ${t.nombre_destinatario ?? ""} ${t.direccion ?? ""} ${turboChofer(t)} ${t.tracking ?? ""} ${TURBO_ESTADO_LABEL[t.estado]}`),
  };
}

export function rowsFor(kind: LayerKey, data: MapLayersData | null, homes: MapHome[], f: ListFilters, turbos: TurboHoy[] = []): ListRow[] {
  const q = normalize(f.q.trim());
  let rows: ListRow[] = [];
  if (kind === "turbo") {
    // Abiertos primero, el que vence antes arriba; los cerrados al final.
    rows = [...turbos]
      .sort((a, b) => Number(a.terminal) - Number(b.terminal) || (a.seconds_to_deadline ?? 1e9) - (b.seconds_to_deadline ?? 1e9))
      .map(turboRow);
  } else if (kind === "pickups" || kind === "deliveries") {
    rows = (data?.stops ?? [])
      .filter((s) => s.type === (kind === "pickups" ? "PICKUP" : "DELIVERY"))
      .filter((s) => f.state === "ALL" || s.state === f.state)
      .filter((s) => f.driverId === "ALL" || s.driver_id === f.driverId)
      .map(stopRow);
  } else if (kind === "stores") {
    const today = storesWithPickup(data?.stops ?? []);
    rows = (data?.stores ?? [])
      .filter((s) => f.storeToday === "ALL" || (f.storeToday === "WITH") === today.has(s.id))
      .map((s) => ({
        kind, id: s.id, title: s.name, color: "#db61a2", lat: s.latitude, lng: s.longitude,
        subtitle: [s.address, today.has(s.id) ? `colecta hoy (${today.get(s.id)})` : "sin colecta hoy"].filter(Boolean).join(" · "),
        search: normalize(`${s.name} ${s.address ?? ""}`),
      }));
  } else if (kind === "warehouses") {
    rows = (data?.warehouses ?? []).map((w) => ({
      kind, id: w.id, title: w.name, color: "#e3b341", lat: w.latitude, lng: w.longitude,
      subtitle: [w.type === "DROPOFF" ? "Dropoff" : "Depósito", w.address].filter(Boolean).join(" · "),
      search: normalize(`${w.name} ${w.address ?? ""}`),
    }));
  } else if (kind === "zones") {
    rows = (data?.zones ?? []).map((z, i) => {
      const c = zoneCenter(z);
      return { kind, id: z.id, title: z.name, subtitle: `${z.rings.length} polígono${z.rings.length === 1 ? "" : "s"}`,
        color: ZONE_COLORS[i % ZONE_COLORS.length], lat: c?.[0] ?? null, lng: c?.[1] ?? null, search: normalize(z.name) };
    });
  } else if (kind === "homes") {
    rows = homes.map((h) => ({
      kind, id: h.driver_id, title: homeName(h), subtitle: h.address, color: "#f0883e", lat: h.latitude, lng: h.longitude,
      driverId: h.driver_id, search: normalize(`${homeName(h)} ${h.username} ${h.address}`),
    }));
  }
  return q ? rows.filter((r) => r.search.includes(q)) : rows;
}

// Las paradas de un chofer en el orden en que las hace (por recorrido).
export function driverStops(stops: MapStop[], driverId: number): MapStop[] {
  return stops.filter((s) => s.driver_id === driverId).sort((a, b) => a.route_id - b.route_id || a.sequence - b.sequence);
}
